import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LifeBuoy,
  ChevronDown,
  Ticket,
  MessageSquare,
  Mail,
  KeyRound,
  UserPlus,
  ArrowRight,
} from 'lucide-react';

const Support = () => {
  const [openFaq, setOpenFaq] = useState(0);

  const faqs = [
    {
      question: 'How do I create a support ticket?',
      answer:
        'Log in to your account, go to the Tickets section, and click "New Ticket". Describe your issue, pick a category and priority, and submit. You can track its status and chat with our team from the ticket page at any time.',
    },
    {
      question: 'I forgot my password. How do I reset it?',
      answer:
        'On the login page, click "Forgot Password" and enter your registered email address. We will send you a link to set a new password. If the email does not arrive, check your spam folder.',
    },
    {
      question: 'How do I know when my ticket is updated?',
      answer:
        'You will receive an in-app notification whenever your ticket is assigned, replied to, escalated, or resolved. You can view all notifications from the bell icon after logging in.',
    },
    {
      question: 'What do the ticket priorities mean?',
      answer:
        'Priorities help our team triage: Low for general questions, Medium for issues with a workaround, High for problems blocking your work, and Urgent for critical outages. Higher priorities have faster response targets.',
    },
    {
      question: 'Can I chat with the support agent directly?',
      answer:
        'Yes — every ticket includes a built-in real-time chat. Open your ticket and use the chat panel to talk with the assigned agent without leaving the page.',
    },
    {
      question: 'How do I create an account?',
      answer:
        'Click Register on the top navigation, fill in your details, and accept the Terms of Service. Once registered, you can log in and start raising tickets immediately.',
    },
  ];

  const quickActions = [
    {
      icon: UserPlus,
      title: 'Create an Account',
      description: 'New here? Register in under a minute.',
      to: '/register',
      cta: 'Register',
    },
    {
      icon: Ticket,
      title: 'Open a Ticket',
      description: 'Log in and raise a ticket for the fastest help.',
      to: '/login',
      cta: 'Log In',
    },
    {
      icon: KeyRound,
      title: 'Reset Password',
      description: 'Locked out? Recover access to your account.',
      to: '/forgot-password',
      cta: 'Reset',
    },
    {
      icon: Mail,
      title: 'Contact Us',
      description: 'General questions, sales, or partnerships.',
      to: '/contact',
      cta: 'Contact',
    },
  ];

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="h-14 w-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-6">
            <LifeBuoy className="h-7 w-7 text-primary-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900">
            Support <span className="text-primary-600">Center</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Find answers to common questions, or reach out — we're here to help.
          </p>
        </div>

        {/* Quick Actions */}
        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {quickActions.map((action) => {
            const Icon = action.icon;
            return (
              <div
                key={action.title}
                className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col hover:shadow-md transition-shadow"
              >
                <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center mb-4">
                  <Icon className="h-5 w-5 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900">{action.title}</h3>
                <p className="text-sm text-gray-600 mt-1 flex-grow">{action.description}</p>
                <Link
                  to={action.to}
                  className="mt-4 inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700"
                >
                  {action.cta}
                  <ArrowRight className="h-4 w-4 ml-1" />
                </Link>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={faq.question}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? -1 : index)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-gray-50 transition-colors"
                >
                  <span className="font-medium text-gray-900 pr-4">{faq.question}</span>
                  <ChevronDown
                    className={`h-5 w-5 text-gray-500 flex-shrink-0 transition-transform ${
                      openFaq === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed border-t border-gray-100 pt-4">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Still need help */}
        <div className="mt-16 bg-primary-600 rounded-2xl p-8 sm:p-12 text-center">
          <MessageSquare className="h-10 w-10 text-primary-200 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-white">Still need help?</h2>
          <p className="mt-3 text-primary-100 max-w-xl mx-auto">
            Log in and open a ticket — our team will pick it up and keep you posted every step
            of the way.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/login"
              className="inline-flex items-center bg-white text-primary-700 hover:bg-primary-50 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Log In & Open a Ticket
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center text-white border border-primary-300 hover:bg-primary-500 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Support;
